// Tabs Component
class TabsCustom extends HTMLElement {
  /** @type {HTMLElement} */
  get tabList() {
    return this.querySelector('.tabs__list');
  }

  /** @type {HTMLElement} */
  get panelsContainer() {
    return this.querySelector('.tabs__panels');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get rows() {
    return this.querySelectorAll('.tabs__row');
  }

  /** @type {NodeListOf<HTMLButtonElement>} */
  get buttons() {
    return this.querySelectorAll('.tabs__button');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get panels() {
    return this.querySelectorAll('.tabs__panel');
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    // Build tabs from rows
    this.#buildTabs();

    // Add click handlers to all buttons
    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.#handleTabClick(index), { signal });
      button.addEventListener('keydown', (e) => this.#handleKeyDown(e, index), { signal });
    });

    // Set initial active tab
    if (this.buttons.length > 0) {
      this.#setActiveTab(0);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Builds tabs from row elements
   */
  #buildTabs() {
    const rows = Array.from(this.rows);
    if (rows.length === 0) return;

    // Clear existing buttons to prevent duplication
    this.tabList.innerHTML = '';

    // Create tab buttons
    rows.forEach((row, index) => {
      const heading = row.getAttribute('data-tab-heading');
      const tabId = row.getAttribute('data-tab-id');
      const buttonId = `tab-button-${tabId}`;

      // Create button
      const button = document.createElement('button');
      button.className = 'tabs__button';
      button.id = buttonId;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('aria-controls', tabId);
      button.setAttribute('data-tab-index', index.toString());
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
      button.textContent = heading;

      this.tabList.appendChild(button);

      // Convert row to panel
      row.className = 'tabs__panel';
      row.id = tabId;
      row.setAttribute('role', 'tabpanel');
      row.setAttribute('aria-labelledby', buttonId);
      if (index !== 0) {
        row.setAttribute('hidden', '');
      }

      // Move content from row-content to panel
      const content = row.querySelector('.tabs__row-content');
      if (content) {
        while (content.firstChild) {
          row.appendChild(content.firstChild);
        }
        content.remove();
      }
    });
  }

  /**
   * Handles tab button click
   * @param {number} index - The index of the clicked tab
   */
  #handleTabClick(index) {
    this.#setActiveTab(index);
  }

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   * @param {number} currentIndex - The current tab index
   */
  #handleKeyDown(event, currentIndex) {
    let targetIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        targetIndex = currentIndex > 0 ? currentIndex - 1 : this.buttons.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        targetIndex = currentIndex < this.buttons.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = this.buttons.length - 1;
        break;
      default:
        return;
    }

    this.#setActiveTab(targetIndex);
    this.buttons[targetIndex].focus();
  }

  /**
   * Sets the active tab
   * @param {number} index - The index of the tab to activate
   */
  #setActiveTab(index) {
    const buttons = Array.from(this.buttons);
    const panels = Array.from(this.panels);

    // Remove active state from all buttons and panels
    buttons.forEach((button) => {
      button.classList.remove('tabs__button--active');
      button.setAttribute('aria-selected', 'false');
      button.setAttribute('tabindex', '-1');
    });

    panels.forEach((panel) => {
      panel.classList.remove('tabs__panel--active');
      panel.setAttribute('hidden', '');
    });

    // Add active state to selected button and panel
    if (buttons[index] && panels[index]) {
      buttons[index].classList.add('tabs__button--active');
      buttons[index].setAttribute('aria-selected', 'true');
      buttons[index].setAttribute('tabindex', '0');
      panels[index].classList.add('tabs__panel--active');
      panels[index].removeAttribute('hidden');
    }
  }
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}
